import type { UserRole } from '@compass/client/types/membership';
import { cn } from '@utils/index';
import { cva, type VariantProps } from 'class-variance-authority';
import { Building2, Folder, Globe, Users } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';

/**
 * Size variants for RoleBadge component
 * Maps to common use cases throughout the app
 */
const roleBadgeVariants = cva('inline-flex items-center gap-1 font-medium', {
  variants: {
    size: {
      sm: 'text-xs px-2 py-0.5', // Compact lists, tables
      md: 'text-sm px-2.5 py-0.5', // Default, most common use
      lg: 'text-base px-3 py-1', // Prominent displays
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/**
 * Icon size variants to match badge sizes
 */
const iconSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'h-3 w-3',
      md: 'h-3.5 w-3.5',
      lg: 'h-4 w-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface RoleBadgeProps extends VariantProps<typeof roleBadgeVariants> {
  /**
   * The user role object containing role details and assignment context
   */
  role: UserRole;

  /**
   * Optional additional CSS classes
   */
  className?: string;

  /**
   * Whether to show the level icon (global/org/program)
   * @default true
   */
  showIcon?: boolean;

  /**
   * Whether to show the role name text
   * @default true
   */
  showName?: boolean;

  /**
   * Visual variant of the badge
   * - default: Pill-style badge with background color
   * - muted: Text-only with muted foreground color
   * @default "default"
   */
  variant?: 'default' | 'muted';
}

/**
 * Get the appropriate icon component based on role level
 */
const getLevelIcon = (level?: string) => {
  switch (level?.toLowerCase()) {
    case 'global':
      return Globe;
    case 'organization':
      return Building2;
    case 'program':
      return Folder;
    default:
      return Users; // Default to program icon
  }
};

/**
 * Get color classes based on role level
 */
const getLevelColor = (level?: string) => {
  switch (level?.toLowerCase()) {
    case 'organization':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    case 'program':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'global':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

/**
 * RoleBadge - A badge component for displaying user roles with color and icon
 *
 * Features:
 * - Deterministic color assignment based on role name hash (same as UserAvatar)
 * - Level-based icon display (Globe for global, Building2 for org, Users for program)
 * - Multiple size variants (sm, md, lg)
 * - Two visual variants: pill-style badge or muted text-only
 * - Matches the color palette used by UserAvatar
 *
 * @example
 * ```tsx
 * <RoleBadge role={userRole} size="md" />
 * <RoleBadge role={userRole} size="sm" showIcon={false} />
 * <RoleBadge role={userRole} variant="muted" />
 * ```
 */
export const RoleBadge: React.FC<RoleBadgeProps> = ({
  role,
  size,
  className,
  showIcon = true,
  showName = true,
  variant = 'default',
}) => {
  const roleName = role.name || 'Unknown Role';
  const colorClasses = getLevelColor(role.level);
  const Icon = getLevelIcon(role.level);

  // For muted variant, render text-only with muted foreground color
  if (variant === 'muted') {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1 font-medium pointer-events-none cursor-default whitespace-nowrap text-muted-foreground',
          size === 'sm' && 'text-xs',
          size === 'md' && 'text-sm',
          size === 'lg' && 'text-base',
          className,
        )}
      >
        {showIcon && <Icon className={iconSizeVariants({ size })} />}
        {showName && <span>{roleName}</span>}
      </div>
    );
  }

  // Default variant: pill-style badge with border (border color included in colorClasses)
  return (
    <Badge
      variant="secondary"
      className={cn(
        roleBadgeVariants({ size }),
        colorClasses,
        'pointer-events-none cursor-default whitespace-nowrap border-none',
        className,
      )}
    >
      {showIcon && <Icon className={iconSizeVariants({ size })} />}
      {showName && <span>{roleName}</span>}
    </Badge>
  );
};
